import { Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MessageText } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { AuthFormLayout, OtpInput, SefanoButton } from "@/components";
import { formatOtpTimer, formatPhoneDisplay } from "@/lib/format/digits";
import type { OtpVerifyTemplateProps } from "./OtpVerify.template.types";
import * as styles from "./OtpVerify.template.styles";

export type { OtpVerifyTemplateProps } from "./OtpVerify.template.types";

export function OtpVerifyTemplate({
  phone,
  code,
  onCodeChange,
  onSubmit,
  onResend,
  seconds,
  isExpired,
  isPending,
  isResending,
  errorMessage,
}: OtpVerifyTemplateProps) {
  const { t } = useTranslation("auth");
  const theme = useTheme();

  return (
    <AuthFormLayout
      showLogo={false}
      backTo="/otp"
      headerSpacing="spacious"
      headerIcon={<MessageText size={34} variant="Bold" color={theme.palette.primary.main} />}
      title={t("otp_verify_title")}
      subtitle={
        <>
          {t("otp_verify_subtitle")}
          <br />
          <Typography component="span" sx={styles.phoneHighlight}>
            {formatPhoneDisplay(phone)}
          </Typography>{" "}
          {t("otp_verify_subtitle_suffix")}
        </>
      }
    >
      <OtpInput value={code} onChange={onCodeChange} error={Boolean(errorMessage)} disabled={isPending} />

      {errorMessage ? (
        <Typography variant="caption" color="error" sx={styles.errorText}>
          {errorMessage}
        </Typography>
      ) : null}

      <Typography sx={styles.timerLine}>
        {isExpired ? (
          <>
            {t("otp_not_received")}{" "}
            <Typography
              component="span"
              sx={styles.resendAction}
              onClick={isResending ? undefined : onResend}
            >
              {isResending ? t("loading") : t("resend_code")}
            </Typography>
          </>
        ) : (
          <>
            {t("resend_countdown_prefix")} {formatOtpTimer(seconds)}
          </>
        )}
      </Typography>

      <Stack component="form" onSubmit={(event) => event.preventDefault()}>
        <SefanoButton fullWidth disabled={isPending || code.length !== 5} onClick={onSubmit}>
          {isPending ? t("loading") : t("verify_and_login")}
        </SefanoButton>
      </Stack>
    </AuthFormLayout>
  );
}
